import styled from '@emotion/styled'

import { Color } from '../../enums/color'
import { LineHeight } from '../../enums/lineHeight'
import { Spacing } from '../../enums/spacing'
import { ControlAlignmentPlacement } from '../../types/controlAlignmentPlacement'
import { getControlAlignmentPlacement } from '../../utils/getControlAlignmentPlacement'
import { getDefaultTransition } from '../../utils/getDefaultTransition'
import { Label as LabelComponent } from '../Label'
import { Size } from './types/size'
import { getCheckedStyle } from './utils/getCheckedStyle'
import { getDisabledStyle } from './utils/getDisabledStyle'
import { getErrorStyle } from './utils/getErrorStyle'
import { getHoverStyle } from './utils/getHoverStyle'
import { getPressedStyle } from './utils/getPressedStyle'
import { sizeToProperties } from './utils/sizeToProperties'

interface ControlProps {
  size: Size
  controlAlignmentPlacement?: ControlAlignmentPlacement
}

export const Control = styled.span<ControlProps>`
  ${({ controlAlignmentPlacement }) => getControlAlignmentPlacement(controlAlignmentPlacement)};
  display: flex;
  align-items: center;
  align-self: center;
  flex-shrink: 0;
  justify-content: center;
  width: ${({ size }) => sizeToProperties[size].dimension}px;
  height: ${({ size }) => sizeToProperties[size].dimension}px;
  border: 2px solid ${Color.Grey200};
  border-radius: ${({ size }) => sizeToProperties[size].radius};
  background-color: ${Color.White};
  ${getDefaultTransition('background', 'border')};

  ${() => IconWrapper} {
    svg {
      transform: scale(${({ size }) => sizeToProperties[size].iconScale});
      transform-origin: center;
    }
  }
`

export const IconWrapper = styled.figure`
  display: flex;
`

export const Label = styled(LabelComponent)`
  margin-left: ${Spacing.S8};
  cursor: inherit;
  ${getDefaultTransition('color')};
`

export const NativeCheckbox = styled.input`
  opacity: 0;
  width: 1px;
  height: 1px;
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
`

interface CheckboxWrapperProps {
  checked: boolean
  disabled: boolean
  error: boolean
  indeterminate: boolean
}

/* Keeps checkbox centrally aligned with the first line of the label */
export const ControlWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  /* same line height with the checkbox label */
  height: ${LineHeight.TextBodyMD};
`

export const CheckboxWrapper = styled.label<CheckboxWrapperProps>`
  display: flex;
  position: relative;
  cursor: pointer;

  path,
  rect {
    fill: currentColor;
  }

  &:has(:focus-visible) {
    ${() => Control} {
      outline: 2px solid ${Color.Black};
      outline-offset: 2px;
    }
  }

  ${({ checked, indeterminate, disabled, error }) =>
    !disabled && !error && getHoverStyle(Control, ControlWrapper, checked, indeterminate)};
  ${({ checked, disabled, error }) => !disabled && !error && getPressedStyle(Control, ControlWrapper, checked)};
  ${({ checked, indeterminate }) => (checked || indeterminate) && getCheckedStyle(Control)};
  ${({ checked, error }) => error && getErrorStyle(Control, checked)};
  ${({ checked, disabled }) => disabled && getDisabledStyle(Control, Label, checked)};
`
